<template>
  <v-card v-if="visible" class="stage-card mb-4" outlined>
    <v-card-title v-if="nextStep" class="stage-card-title">
      {{ $t(`stage_card.next_step.${nextStep.key}`) }}
    </v-card-title>
    <v-card-text>
      <v-list dense>
        <v-list-item
          v-for="row in rows"
          :key="row.key"
          :class="'stage-card-milestone stage-card-milestone-' + row.key"
          :ripple="!row.done"
          @click="row.done ? null : goTo(row.route)"
        >
          <v-list-item-icon>
            <v-icon v-if="row.done" color="success" class="stage-card-milestone-done">mdi-check-circle</v-icon>
            <v-icon v-else class="stage-card-milestone-pending">mdi-checkbox-blank-circle-outline</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title
              class="stage-card-milestone-title"
              :class="row.done ? 'text-decoration-line-through' : ''"
              style="white-space: normal; word-wrap: break-word;"
            >
              {{ $t(`stage_card.milestones.${row.key}`) }}
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </v-list>
    </v-card-text>
    <v-card-actions>
      <v-btn v-if="docsHref" class="stage-card-how-to" text color="primary" :href="docsHref" target="_blank" rel="noopener">
        {{ $t("stage_card.how_to") }}
      </v-btn>
      <v-spacer></v-spacer>
      <v-btn class="stage-card-hide" text @click="hide">
        {{ $t("stage_card.hide") }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
// FT-170 STEP-11. Карточка «первые шаги» — статичный блок над grid-layout
// (дизайн §3), не виджет: раскладкой не убирается, только кнопкой «Скрыть».
// Вся видимость/данные вех — через чистые функции src/utils/stageCard.js,
// чтобы их можно было проверить без монтирования.
import { shouldShowStageCard, docsUrl, milestoneRows } from "@/utils/stageCard";
import { docsBase } from "@/utils/docsSite";
import { ADMIN_PLUS_ROLES } from "@/constants/roles";

export const STAGE_PATH = "/api/v1/company_stage";
export const UPDATE_USER_SETTING_PATH = "/api/v1/update_user_setting";

export default {
  name: "StageCard",
  props: {
    // Явный базовый адрес справки (тесты). Без него — по стране филиала и языку
    // интерфейса (FT-205): вычисляемым свойством, а не фабрикой умолчания пропа —
    // та считается один раз при создании, а на главной карточка может появиться
    // раньше, чем загрузится филиал.
    docsBaseUrl: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      stage: null,
      milestones: null,
      nextStep: null,
      // FM-08: любая ошибка эндпоинта (403/404/500/сеть) — карточки нет, без
      // баннера. loadFailed — единственный след отказа, наружу не показывается.
      loadFailed: false,
      // Запрос уже отправлен — смена роли (admin ↔ superadmin) его не повторяет.
      loadRequested: false,
    };
  },
  computed: {
    role() {
      const user = this.$store.getters.getCurrentUser;
      return user && user.role;
    },
    hideStageCard() {
      const settings = this.$store.getters.getUserSettings;
      return !!(settings && settings.hide_stage_card);
    },
    visible() {
      if (this.loadFailed) return false;
      return shouldShowStageCard({
        role: this.role,
        stage: this.stage,
        hideStageCard: this.hideStageCard,
      });
    },
    rows() {
      return milestoneRows(this.milestones);
    },
    effectiveDocsBase() {
      return this.docsBaseUrl || docsBase(this.$store.getters.getCompany?.company, this.$i18n?.locale);
    },
    docsHref() {
      if (!this.nextStep) return null;
      return docsUrl(this.effectiveDocsBase, this.nextStep.docs_path);
    },
  },
  // Финальное ревью, F9: GET /api/v1/company_stage только для superadmin/admin —
  // остальным эндпоинт ответит 403, а карточку они не видят в любом случае.
  // Роль проверяется ДО запроса. watch с immediate, а не mounted: роль может
  // прийти после монтирования (гидрация vuex-persist) — тогда запрос уходит,
  // как только роль стала допустимой, и ровно один раз.
  watch: {
    role: {
      immediate: true,
      handler(role) {
        if (this.loadRequested || !ADMIN_PLUS_ROLES.includes(role)) return;
        this.loadRequested = true;
        this.loadStage();
      },
    },
  },
  methods: {
    loadStage() {
      return this.axios
        .get(STAGE_PATH, {
          headers: { Authorization: this.$store.getters.getAuthToken },
        })
        .then((response) => {
          this.stage = response.data.stage;
          this.milestones = response.data.milestones;
          this.nextStep = response.data.next_step;
        })
        .catch(() => {
          this.loadFailed = true;
        });
    },
    goTo(route) {
      this.$router.push(route);
    },
    // CTR-05: тело — РОВНО одно поле; общий payload user_setting затёр бы
    // остальные 17 настроек (см. Profile.vue updateUserSetting).
    hide() {
      return this.axios
        .patch(
          UPDATE_USER_SETTING_PATH,
          { user_setting: { hide_stage_card: true } },
          { headers: { Authorization: this.$store.getters.getAuthToken } }
        )
        .then((response) => {
          this.$store.commit("setUserSettings", response.data);
        })
        .catch(() => {});
    },
  },
};
</script>
